import { useResearchStats, type StatsFilter } from "@/hooks/useResearchStats";
import { StatBlock } from "./StatBlock";
import { cn } from "@/lib/utils";

interface Props extends StatsFilter {
  title?: string;
  intro?: string;
  columns?: 2 | 3 | 4;
  size?: "hero" | "standard" | "inline";
  className?: string;
}

export function StatGrid({
  title,
  intro,
  columns = 3,
  size = "standard",
  className,
  ...filter
}: Props) {
  const { data: stats = [], isLoading } = useResearchStats(filter);

  if (isLoading) {
    return (
      <div className={cn("grid gap-6", className)} aria-busy="true">
        {Array.from({ length: columns }).map((_, i) => (
          <div key={i} className="h-48 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (stats.length === 0) return null;

  const colsClass =
    columns === 4
      ? "md:grid-cols-2 lg:grid-cols-4"
      : columns === 2
      ? "md:grid-cols-2"
      : "md:grid-cols-2 lg:grid-cols-3";

  return (
    <section className={cn("space-y-6", className)}>
      {(title || intro) && (
        <header className="text-center max-w-3xl mx-auto space-y-3">
          {title && (
            <h2 className="text-3xl md:text-4xl font-bold text-primary">{title}</h2>
          )}
          {intro && <p className="text-muted-foreground">{intro}</p>}
        </header>
      )}
      <div className={cn("grid gap-6 grid-cols-1", colsClass)}>
        {stats.map((s) => (
          <StatBlock key={s.id} stat={s} size={size} />
        ))}
      </div>
    </section>
  );
}